import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  getAdminLocations,
  saveLocation,
  deleteLocation,
  type LocationPayload,
} from "@/services/adminService";
import type { AdminLocation, AdminLocationType } from "@/types/admin";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import AdminSearchInput from "@/pages/portal/admin/components/AdminSearchInput";
import AdminModal from "@/pages/portal/admin/components/AdminModal";
import {
  adminCard,
  adminTh,
  adminTd,
  adminBtnPrimary,
  adminBtnGhost,
  adminBtnDanger,
  adminIconBtn,
  adminInput,
  adminLabel,
} from "@/pages/portal/admin/config/ui";

const EMPTY: LocationPayload = {
  name: "", slug: "", type: "district", districtName: null, pinCode: "", sortOrder: 1, isActive: true,
};

function slugify(value: string): string {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function LocationsSection() {
  const { accessToken } = useAuth();
  const { data, setData, loading, error, reload } = useAdminResource(
    () => getAdminLocations(accessToken),
    [] as AdminLocation[],
    [accessToken],
  );

  const [tab, setTab]         = useState<AdminLocationType>("district");
  const [search, setSearch]   = useState("");
  const [busyId, setBusyId]   = useState<number | null>(null);
  const [editing, setEditing] = useState<LocationPayload | null>(null);
  const [editId, setEditId]   = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminLocation | null>(null);

  const districts = useMemo(() => data.filter((l) => l.type === "district"), [data]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.filter((l) => {
      if (l.type !== tab) return false;
      if (!q) return true;
      return [l.name, l.slug, l.districtName ?? "", l.pinCode ?? ""].join(" ").toLowerCase().includes(q);
    });
  }, [data, tab, search]);

  function openCreate() {
    setEditing({ ...EMPTY, type: tab, sortOrder: data.filter((l) => l.type === tab).length + 1, districtName: tab === "town" ? districts[0]?.name ?? null : null });
    setEditId(null);
  }

  function openEdit(row: AdminLocation) {
    setEditing({
      name: row.name, slug: row.slug, type: row.type, districtName: row.districtName,
      pinCode: row.pinCode ?? "", sortOrder: row.sortOrder, isActive: row.isActive,
    });
    setEditId(row.id);
  }

  function setField<K extends keyof LocationPayload>(key: K, value: LocationPayload[K]) {
    setEditing((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function submit() {
    if (!editing?.name?.trim()) return;
    const payload: LocationPayload = { ...editing, slug: editing.slug?.trim() || slugify(editing.name) };
    setBusyId(editId ?? -1);
    try {
      await saveLocation(accessToken, payload, editId ?? undefined);
      if (editId) {
        setData((prev) => prev.map((l) => (l.id === editId ? { ...l, ...payload, slug: payload.slug as string } : l)));
      } else {
        setData((prev) => [
          ...prev,
          {
            id: Date.now(), name: payload.name as string, slug: payload.slug as string,
            type: payload.type ?? tab, districtName: payload.districtName ?? null,
            pinCode: payload.pinCode || null, businessCount: 0,
            sortOrder: payload.sortOrder ?? prev.length + 1, isActive: payload.isActive ?? true,
          },
        ]);
      }
      setEditing(null);
    } finally {
      setBusyId(null);
    }
  }

  async function toggleActive(row: AdminLocation) {
    const next = !row.isActive;
    setData((prev) => prev.map((l) => (l.id === row.id ? { ...l, isActive: next } : l)));
    // Always send the type so the backend edits the right table (districts vs towns).
    await saveLocation(accessToken, { isActive: next, type: row.type }, row.id);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await deleteLocation(accessToken, deleteTarget.id, deleteTarget.type);
      setData((prev) => prev.filter((l) => l.id !== deleteTarget.id));
      setDeleteTarget(null);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <AdminSearchInput value={search} onChange={setSearch} placeholder={`Search ${tab}s…`} />
        {/* Type tabs */}
        <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1">
          {(["district", "town"] as AdminLocationType[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`cursor-pointer rounded-full px-3 py-1 text-xs font-semibold capitalize transition-colors ${
                tab === t ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              {t === "district" ? `Districts (${districts.length})` : `Towns (${data.length - districts.length})`}
            </button>
          ))}
        </div>
        <button type="button" onClick={openCreate} className={adminBtnPrimary}>
          <i className="ri-add-line text-sm" /> New {tab}
        </button>
      </div>

      {error && <AdminDataError message={error} onRetry={reload} />}

      <div className={adminCard}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-map-pin-2-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700">No {tab}s found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse">
              <thead className="border-b border-background-200">
                <tr>
                  <th className={adminTh}>{tab === "district" ? "District" : "Town"}</th>
                  {tab === "town" && <th className={adminTh}>District</th>}
                  {tab === "town" && <th className={adminTh}>PIN</th>}
                  <th className={adminTh}>Slug</th>
                  <th className={adminTh}>Listings</th>
                  <th className={adminTh}>Order</th>
                  <th className={adminTh}>Visible</th>
                  <th className={`${adminTh} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {rows.map((l) => (
                  <tr key={l.id} className="hover:bg-background-100/40">
                    <td className={`${adminTd} font-semibold text-foreground-900`}>{l.name}</td>
                    {tab === "town" && <td className={adminTd}>{l.districtName ?? "—"}</td>}
                    {tab === "town" && <td className={`${adminTd} tabular-nums`}>{l.pinCode ?? "—"}</td>}
                    <td className={adminTd}><code className="rounded bg-background-100 px-1.5 py-0.5 text-[11px] text-foreground-600">{l.slug}</code></td>
                    <td className={`${adminTd} tabular-nums`}>{l.businessCount.toLocaleString("en-IN")}</td>
                    <td className={`${adminTd} tabular-nums`}>{l.sortOrder}</td>
                    <td className={adminTd}>
                      <button
                        type="button"
                        onClick={() => toggleActive(l)}
                        className={`inline-flex cursor-pointer items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold transition-colors ${
                          l.isActive ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-background-200 text-foreground-600 hover:bg-background-300"
                        }`}
                      >
                        {l.isActive ? "Visible" : "Hidden"}
                      </button>
                    </td>
                    <td className={`${adminTd} text-right`}>
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" onClick={() => openEdit(l)} className={adminIconBtn} title="Edit"><i className="ri-pencil-line text-base" /></button>
                        <button type="button" onClick={() => setDeleteTarget(l)} className={adminIconBtn} title="Delete"><i className="ri-delete-bin-line text-base text-rose-600" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <AdminModal
          title={editId ? `Edit ${editing.type}` : `New ${editing.type}`}
          onClose={() => setEditing(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEditing(null)} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={!editing.name?.trim() || busyId !== null} onClick={submit} className={adminBtnPrimary}>
                {editId ? "Save changes" : "Create"}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            {editing.type === "town" && (
              <div>
                <label className={adminLabel} htmlFor="loc-district">District</label>
                <select id="loc-district" value={editing.districtName ?? ""} onChange={(e) => setField("districtName", e.target.value)} className={`${adminInput} cursor-pointer`}>
                  <option value="">Select district…</option>
                  {districts.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className={adminLabel} htmlFor="loc-name">Name</label>
              <input id="loc-name" type="text" value={editing.name ?? ""} onChange={(e) => setField("name", e.target.value)} className={adminInput} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={adminLabel} htmlFor="loc-slug">Slug</label>
                <input id="loc-slug" type="text" value={editing.slug ?? ""} onChange={(e) => setField("slug", e.target.value)} placeholder="auto-generated" className={adminInput} />
              </div>
              {editing.type === "town" ? (
                <div>
                  <label className={adminLabel} htmlFor="loc-pin">PIN code</label>
                  <input id="loc-pin" type="text" value={editing.pinCode ?? ""} onChange={(e) => setField("pinCode", e.target.value)} className={adminInput} />
                </div>
              ) : (
                <div>
                  <label className={adminLabel} htmlFor="loc-order">Sort order</label>
                  <input id="loc-order" type="number" min={1} value={editing.sortOrder ?? 1} onChange={(e) => setField("sortOrder", Number(e.target.value) || 1)} className={adminInput} />
                </div>
              )}
            </div>
            {editing.type === "town" && (
              <div>
                <label className={adminLabel} htmlFor="loc-order-t">Sort order</label>
                <input id="loc-order-t" type="number" min={1} value={editing.sortOrder ?? 1} onChange={(e) => setField("sortOrder", Number(e.target.value) || 1)} className={adminInput} />
              </div>
            )}
            <label className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground-700">
              <input type="checkbox" checked={!!editing.isActive} onChange={(e) => setField("isActive", e.target.checked)} className="h-4 w-4 cursor-pointer accent-primary-500" />
              Visible on the website
            </label>
          </div>
        </AdminModal>
      )}

      {deleteTarget && (
        <AdminModal
          title={`Delete ${deleteTarget.type}`}
          subtitle={deleteTarget.name}
          onClose={() => setDeleteTarget(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteTarget(null)} className={adminBtnGhost}>Cancel</button>
              <button type="button" disabled={busyId === deleteTarget.id} onClick={confirmDelete} className={adminBtnDanger}>
                <i className="ri-delete-bin-line text-sm" /> Delete
              </button>
            </div>
          }
        >
          <p className="text-sm text-foreground-700">
            Delete <strong>{deleteTarget.name}</strong>? Listings assigned to this location must be reassigned first.
          </p>
        </AdminModal>
      )}
    </div>
  );
}